import { useEffect, useState } from "react"
import { TShoppingTicket } from "../../utils/@types/data/ticket"
import * as S from "./styled"

import { useNavigate } from "react-router-dom"
import { Download, Maximize2, MessageCircle, X } from "lucide-react"

import AskPhoneNumberModal from "../Modal/AskPhoneNumber"
import { Api } from "../../api"
import { onlyDigits } from "../../utils/tb/phone/onlyDigits"
import getStore from "../../store"
import downloadTickets from "../../utils/pdf"
import { formatMoney } from "../../utils/tb/formatMoney"
import { getProductTaxes } from "../../utils/tb/taxes"
import { formatDate } from "date-fns"
import { buildTicketsLink } from "../../utils/tb/ticketsLink"

type Props = {
  data: any
  k: number
}

const TicketCard = ({ k, data }: Props) => {
  const { event, user } = getStore()
  const navigate = useNavigate()

  const [isCancelled, setIsCancelled] = useState(false)
  const [tickets, setTickets] = useState<TShoppingTicket[]>([])
  const [askingPhone, setAskingPhone] = useState(false)
  const [sendError, setSendError] = useState("")
  const [sentTo, setSentTo] = useState<string[]>([])

  useEffect(() => {
    setIsCancelled(data.status === "cancelamento")
    if (Array.isArray(data.products)) setTickets(data.products)
  }, [data])

  const getOrderId = () =>
    String(data.id ?? data.order_id ?? data.products?.[0]?.order_id ?? "")

  const handleViewTickets = () => {
    if (isCancelled || !event) return

    const orderId = getOrderId()
    if (!orderId) return

    // Sem ?k=: este card só aparece com a sessão da conta, que já lê o pedido,
    // e o token na URL ficaria visível para os trackers da página (GA/Pixel).
    const link = new URL(buildTicketsLink(event.name, orderId))
    navigate(link.pathname)
  }

  const handleOpenWhatsapp = () => {
    if (isCancelled || !event || !getOrderId()) return
    setSendError("")
    setAskingPhone(true)
  }

  const handleSendWhatsapp = async (phone: string): Promise<boolean> => {
    if (!event) return false
    setSendError("")
    const digits = onlyDigits(phone)
    const res = await Api.post.voucher.sendWhatsapp({
      orderId: getOrderId(),
      eventId: String(event.id),
      phone: digits,
    })

    if (!res.ok) {
      setSendError(res.error)
      return false
    }

    setSentTo((prev) => (prev.includes(digits) ? prev : [...prev, digits]))
    return true
  }

  const handleDownload = async () => {
    if (isCancelled) return
    if (event) await downloadTickets(event, tickets, true)
  }

  const getDate = () => {
    return formatDate(data.date, "dd/MM/yyyy")
  }

  const getOrderRef = () => {
    const ref = data.extref ? String(data.extref) : ""
    if (!ref) return null
    const short = ref.length > 6 ? ref.slice(-6).toUpperCase() : ref.toUpperCase()
    return `#${short}`
  }

  const getTotalWithTaxes = () => {
    const base = Number(data.total_price) || 0
    if (!event) return base

    const additional = tickets.reduce(
      (sum, t: any) => sum + getProductTaxes(event, t).additionalTax,
      0,
    )

    return base + additional
  }

  const orderRef = getOrderRef()
  const quantity = Number(data.quantity ?? tickets.length ?? 0)

  return (
    <>
      <AskPhoneNumberModal
        shown={askingPhone}
        basePhone={user?.fone ?? ""}
        handleClose={() => setAskingPhone(false)}
        handleConfirm={handleSendWhatsapp}
        sentPhones={sentTo}
        error={sendError}
      />
      <S.Component
        $k={k}
        $isCancelled={isCancelled}
        aria-label={
          isCancelled
            ? `Compra cancelada de ${event?.name ?? "evento"}`
            : `Compra de ${event?.name ?? "evento"}`
        }
      >
      {isCancelled && (
        <S.StatusBadge role="status" aria-label="Compra cancelada">
          <X aria-hidden="true" />
          Cancelada
        </S.StatusBadge>
      )}

      <S.CardContent $isCancelled={isCancelled}>
        <S.ImageContainer $isCancelled={isCancelled}>
          <img src={data.eventBanner} alt="" />
        </S.ImageContainer>

        <S.EventInfo>
          <div>
            {orderRef && <S.Eyebrow>{orderRef}</S.Eyebrow>}
            <S.EventName>{event?.name}</S.EventName>
            <S.MetaList>
              <S.MetaRow>Data da compra: {getDate()}</S.MetaRow>
              <S.MetaRow>Valor: {formatMoney(getTotalWithTaxes(), true)}</S.MetaRow>
            </S.MetaList>
          </div>

          <S.CardFooter>
            <S.Icons
              className="iconsArea"
              $disabled={isCancelled}
              aria-hidden={isCancelled ? "true" : undefined}
            >
              <button
                type="button"
                onClick={handleDownload}
                disabled={isCancelled}
                aria-disabled={isCancelled}
                aria-label="Baixar ingressos em PDF"
              >
                <Download aria-hidden="true" />
                <span>Baixar</span>
              </button>
              <button
                type="button"
                onClick={handleViewTickets}
                disabled={isCancelled}
                aria-disabled={isCancelled}
                aria-label="Ver ingressos"
              >
                <Maximize2 aria-hidden="true" />
                <span>Ver ingressos</span>
              </button>
              <button
                type="button"
                onClick={handleOpenWhatsapp}
                disabled={isCancelled}
                aria-disabled={isCancelled}
                aria-label="Receber ingressos no WhatsApp"
              >
                <MessageCircle aria-hidden="true" />
                <span>WhatsApp</span>
              </button>
            </S.Icons>

            <S.TicketsQntWrapper>
              <S.TicketsQntNumber>{quantity}</S.TicketsQntNumber>
              <S.TicketsQntLabel>
                {quantity === 1 ? "ingresso" : "ingressos"}
              </S.TicketsQntLabel>
            </S.TicketsQntWrapper>
          </S.CardFooter>
        </S.EventInfo>
      </S.CardContent>
    </S.Component>
    </>
  )
}

export default TicketCard
